import { Backdrop } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { memo, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
    Color,
    FrontSide,
    ShaderMaterial,
    Vector2,
    Vector3,
} from 'three';
import type { Group, Texture } from 'three';
import {
    SHADOW_ALPHA_TEST,
    SHADOW_COLOR_BLEND,
    SHADOW_OPACITY,
    SHADOW_RESOLUTION,
} from '../../Shadows/config';
import CycloramaFloorReflection from './CycloramaFloorReflection';
import { getStudioCycloramaReflectionOffset } from './PedestalGeometry';

const CYCLORAMA_DEPTH_OFFSET = 1.45;
const CYCLORAMA_FLOOR = 3;
const CYCLORAMA_SEGMENTS = 32;

interface Props {
    color: string
    depth: number
    floorY?: number
    floorOverlayRadius?: number
    glossiness: number
    height: number
    radius: number
    reflectionRevision?: number
    shadowTexture?: Texture
}

/**
 * Project the flat cached shadow into world X/Z on both the floor and curved
 * Backdrop. The fragment pass progressively blurs and fades the bake as it
 * climbs the curve, preventing a sharp texture seam on the wall.
 */
function createCycloramaShadowMaterial():ShaderMaterial {
    return new ShaderMaterial({
        depthWrite: false,
        fragmentShader: `
            uniform sampler2D shadowMap;
            uniform vec2 shadowTexelSize;
            uniform vec3 shadowColor;
            uniform float opacity;
            uniform float alphaTest;
            uniform float colorBlend;
            varying vec2 vShadowUv;
            varying float vCurveAmount;

            float sampleShadow(vec2 uv) {
                return texture2D(shadowMap, uv).r;
            }

            void main() {
                float inBounds =
                    step(0.0, vShadowUv.x)
                    * step(vShadowUv.x, 1.0)
                    * step(0.0, vShadowUv.y)
                    * step(vShadowUv.y, 1.0);

                float centerShadow = sampleShadow(vShadowUv);
                float blurRadius = mix(
                    1.5,
                    7.0,
                    smoothstep(0.0, 0.7, vCurveAmount)
                );
                vec2 blurStep = shadowTexelSize * blurRadius;
                float blurredShadow = (
                    sampleShadow(vShadowUv) * 4.0
                    + sampleShadow(vShadowUv + vec2(blurStep.x, 0.0))
                    + sampleShadow(vShadowUv - vec2(blurStep.x, 0.0))
                    + sampleShadow(vShadowUv + vec2(0.0, blurStep.y))
                    + sampleShadow(vShadowUv - vec2(0.0, blurStep.y))
                    + sampleShadow(vShadowUv + blurStep) * 0.5
                    + sampleShadow(vShadowUv - blurStep) * 0.5
                    + sampleShadow(vShadowUv + vec2(blurStep.x, -blurStep.y)) * 0.5
                    + sampleShadow(vShadowUv + vec2(-blurStep.x, blurStep.y)) * 0.5
                ) / 10.0;
                float curveTransition =
                    smoothstep(0.002, 0.35, vCurveAmount);
                float sampledShadow = mix(
                    centerShadow,
                    blurredShadow,
                    curveTransition
                );
                float whitePoint = max(
                    max(
                        sampleShadow(vec2(0.05, 0.05)),
                        sampleShadow(vec2(0.95, 0.05))
                    ),
                    max(
                        sampleShadow(vec2(0.05, 0.95)),
                        sampleShadow(vec2(0.95, 0.95))
                    )
                );
                float shadow = clamp(
                    sampledShadow / max(whitePoint, 0.0001),
                    0.0,
                    1.0
                );
                float curveStrength = mix(1.0, 0.42, curveTransition);
                float wallFade =
                    1.0 - smoothstep(0.62, 0.98, vCurveAmount);
                float receiverMask = curveStrength * wallFade;
                float distanceToCenter = max(
                    abs(vShadowUv.x - 0.5),
                    abs(vShadowUv.y - 0.5)
                ) * 2.0;
                float edgeMask = 1.0 - smoothstep(0.72, 1.0, distanceToCenter);
                float shadowOpacity =
                    (1.0 - shadow)
                    * opacity
                    * receiverMask
                    * edgeMask
                    * inBounds;

                gl_FragColor = vec4(
                    shadowColor * shadow * (alphaTest / 3.0) * colorBlend,
                    shadowOpacity
                );
                #include <tonemapping_fragment>
                #include <colorspace_fragment>
            }
        `,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1,
        side: FrontSide,
        toneMapped: true,
        transparent: true,
        uniforms: {
            alphaTest: {value: SHADOW_ALPHA_TEST},
            colorBlend: {value: SHADOW_COLOR_BLEND},
            opacity: {value: SHADOW_OPACITY},
            shadowColor: {value: new Color('black')},
            shadowMap: {value: null},
            shadowScale: {value: 1},
            shadowTexelSize: {
                value: new Vector2(
                    1 / SHADOW_RESOLUTION,
                    1 / SHADOW_RESOLUTION,
                ),
            },
        },
        vertexShader: `
            uniform float shadowScale;
            varying vec2 vShadowUv;
            varying float vCurveAmount;

            void main() {
                vec4 worldPosition = modelMatrix * vec4(position, 1.0);
                vShadowUv = vec2(
                    0.5 + worldPosition.x / shadowScale,
                    0.5 - worldPosition.z / shadowScale
                );
                vCurveAmount = position.z;
                gl_Position = projectionMatrix * viewMatrix * worldPosition;
            }
        `,
    });
}

/**
 * Draws the same cyclorama geometry twice: an opaque colored surface and a
 * transparent cached-shadow overlay. The overlay projects a flat bake in world
 * X/Z, then softens and fades it through the curve. Geometry follows camera
 * yaw while the original directional shadow stays world-oriented.
 */
export default memo(function Cyclorama({
    color,
    depth,
    floorY,
    floorOverlayRadius,
    glossiness,
    height,
    radius,
    reflectionRevision,
    shadowTexture,
}:Props) {
    const invalidate = useThree(state => state.invalidate);
    const backdrop = useRef<Group>(null);
    const cameraDirection = useMemo(() => new Vector3(), []);
    const shadowMaterial = useMemo(createCycloramaShadowMaterial, []);
    const position:[number, number, number] = [
        0,
        floorY ?? -height / 2 - radius * 0.015,
        -Math.max(
            radius * CYCLORAMA_DEPTH_OFFSET,
            depth / 2 + radius * 0.65,
        ),
    ];
    const scale:[number, number, number] = [
        radius * 10,
        Math.max(radius * 6, height * 2.5),
        radius * 2,
    ];
    const roughness = Math.max(0.08, 1 - glossiness * 0.92);
    const reflectionInset = Math.max(radius * 0.02, 0.001);
    const floorDepth = CYCLORAMA_FLOOR * scale[2];
    const floorPositionZ = (
        position[2]
        + (0.5 + CYCLORAMA_FLOOR / 2) * scale[2]
    );
    const reflectionDepth = Math.max(floorDepth - reflectionInset * 2, 0.001);
    const reflectionWidth = Math.max(scale[0] - reflectionInset * 2, 0.001);

    useFrame(({camera}) => {
        // The set rotates as a studio rig around a stationary product. Cached
        // shadow UVs remain world-oriented inside the material.
        if(!backdrop.current) return;

        camera.getWorldDirection(cameraDirection);
        backdrop.current.rotation.y = Math.atan2(
            -cameraDirection.x,
            -cameraDirection.z,
        );
    }, -1);

    useLayoutEffect(() => {
        shadowMaterial.uniforms.shadowMap.value = shadowTexture ?? null;
        shadowMaterial.uniforms.shadowScale.value = radius * 4;
        const shadowImage = shadowTexture?.image as {
            height?: number
            width?: number
        } | undefined;
        const shadowTexelSize = shadowMaterial.uniforms.shadowTexelSize.value as Vector2;
        shadowTexelSize.set(
            1 / (shadowImage?.width ?? SHADOW_RESOLUTION),
            1 / (shadowImage?.height ?? SHADOW_RESOLUTION),
        );
        invalidate();
    }, [invalidate, radius, shadowMaterial, shadowTexture]);

    useEffect(() => {
        invalidate();
    }, [color, glossiness, invalidate]);

    useEffect(() => {
        return () => shadowMaterial.dispose();
    }, [shadowMaterial]);

    return <group ref={backdrop}>
        {/* Backdrop supplies the curved wall; the following plane extends its
        short built-in floor far enough to fill low or wide camera framings. */}
        <Backdrop
            floor={0}
            position={position}
            receiveShadow={false}
            scale={scale}
            segments={CYCLORAMA_SEGMENTS}
        >
            <meshPhysicalMaterial
                clearcoat={glossiness}
                clearcoatRoughness={roughness}
                color={color}
                dithering
                metalness={0}
                roughness={roughness}
                side={FrontSide}
            />
        </Backdrop>
        <mesh
            position={[0, position[1], floorPositionZ]}
            receiveShadow={false}
            rotation={[-Math.PI / 2, 0, 0]}
            scale={[scale[0], floorDepth, 1]}
        >
            <planeGeometry />
            <meshPhysicalMaterial
                clearcoat={glossiness}
                clearcoatRoughness={roughness}
                color={color}
                dithering
                metalness={0}
                roughness={roughness}
                side={FrontSide}
            />
        </mesh>
        {glossiness > 0
            ? <CycloramaFloorReflection
                color={color}
                depth={reflectionDepth}
                floorY={
                    position[1]
                    + getStudioCycloramaReflectionOffset(
                        floorOverlayRadius ?? radius,
                    )
                }
                glossiness={glossiness}
                positionZ={floorPositionZ}
                reflectionRevision={reflectionRevision}
                width={reflectionWidth}
            />
            : null}
        {shadowTexture
            ? <>
                {/* Repeat the receiver geometry with a transparent material so
                the cached bake follows both floor and curved transition. */}
                <mesh
                    position={[0, position[1], floorPositionZ]}
                    receiveShadow={false}
                    renderOrder={2}
                    rotation={[-Math.PI / 2, 0, 0]}
                    scale={[scale[0], floorDepth, 1]}
                >
                    <planeGeometry />
                    <primitive attach="material" object={shadowMaterial} />
                </mesh>
                <Backdrop
                    floor={0}
                    position={position}
                    receiveShadow={false}
                    renderOrder={2}
                    scale={scale}
                    segments={CYCLORAMA_SEGMENTS}
                >
                    <primitive attach="material" object={shadowMaterial} />
                </Backdrop>
            </>
            : null}
    </group>;
});
